import { useMemo } from 'react';

interface SubdomainState {
  subdomain: string | null;
  isLandingPage: boolean;
  isResidences: boolean;
  isLoading: boolean;
  isValidSubdomain: boolean;
}

/**
 * Hook to parse and manage subdomain information from the current URL.
 * 
 * Handles:
 * - localhost development with port numbers
 * - www subdomain as landing page
 * - Validates known subdomains against VITE_RESIDENCES_SUBDOMAIN
 */
export function useSubdomain(): SubdomainState {
  const state = useMemo<SubdomainState>(() => {
    // Get environment variables
    const rootDomain = import.meta.env.VITE_ROOT_DOMAIN || 'unidom.az';
    const residencesSubdomain = import.meta.env.VITE_RESIDENCES_SUBDOMAIN || 'residences';
    
    // Get hostname and strip port
    const hostname = typeof window !== 'undefined' 
      ? window.location.hostname.replace(/:\d+$/, '')
      : '';
    
    // Handle localhost development
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isLocalhostWithSubdomain = hostname.endsWith('.localhost') || hostname.endsWith('.127.0.0.1');
    
    if (isLocalhost || isLocalhostWithSubdomain) {
      // Extract subdomain from localhost (e.g., 'residences.localhost' -> 'residences')
      let subdomain: string | null = null;
      
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Plain localhost without subdomain
        subdomain = null;
      } else if (hostname.endsWith('.localhost')) {
        subdomain = hostname.slice(0, hostname.length - '.localhost'.length);
      } else if (hostname.endsWith('.127.0.0.1')) {
        subdomain = hostname.slice(0, hostname.length - '.127.0.0.1'.length);
      }
      
      const isResidences = subdomain === residencesSubdomain;
      const isValidSubdomain = subdomain === null || subdomain === residencesSubdomain;
      
      return {
        subdomain,
        isLandingPage: !subdomain,
        isResidences,
        isLoading: false,
        isValidSubdomain,
      };
    }
    
    // Handle www subdomain as landing page
    if (hostname === 'www' || hostname.startsWith('www.')) {
      return {
        subdomain: null,
        isLandingPage: true,
        isResidences: false,
        isLoading: false,
        isValidSubdomain: true,
      };
    }
    
    // Extract subdomain by removing the root domain
    // Handle both localhost development and production domains
    let subdomain: string | null = null;
    
    if (hostname === rootDomain) {
      // No subdomain (e.g., unidom.az)
      subdomain = null;
    } else if (hostname.endsWith(`.${rootDomain}`)) {
      // Has subdomain (e.g., residences.unidom.az)
      subdomain = hostname.slice(0, hostname.length - rootDomain.length - 1);
    } else {
      // Unknown domain structure - treat the entire hostname as subdomain
      // This handles cases like 'invalid.unidom.az' where rootDomain might differ
      subdomain = hostname;
    }
    
    // Check if subdomain is 'www' (should be treated as landing page)
    const isWww = subdomain === 'www';
    
    if (isWww) {
      return {
        subdomain: null,
        isLandingPage: true,
        isResidences: false,
        isLoading: false,
        isValidSubdomain: true,
      };
    }
    
    // Check if subdomain matches residences
    const isResidences = subdomain === residencesSubdomain;
    
    // Valid subdomains are: null (root domain), 'www', or known subdomains like residences
    const isValidSubdomain = subdomain === null || subdomain === residencesSubdomain;
    
    return {
      subdomain,
      isLandingPage: !subdomain,
      isResidences,
      isLoading: false,
      isValidSubdomain,
    };
  }, []);
  
  return state;
}
